import { useEffect, useRef, useCallback, useMemo } from "react";
import { UseChatbotMonarchReturnType } from "./useChatbotMonarch";
import { showChatIcon, hideChatIcon } from "./helpers/chatbotHelpers";
import { isEmpty } from "lodash";

declare global {
  interface Window {
    KoreChatSDK?: {
      init?: (config: Record<string, unknown>) => void;
      updateConfig?: (config: Record<string, unknown>) => void;
      initialize?: (config: Record<string, unknown>) => void;
      reconfigure?: (config: Record<string, unknown>) => void;
      showChatIcon?: (config: Record<string, unknown>) => void;
      hideChatIcon?: () => void;
      destroy?: () => void;
      updateCustomData?: (data: Record<string, unknown>) => void;
      showMessageAnimationOnChatIcon?: (config: Record<string, unknown>) => void;
      chatConfig?: {
        jwtGrantSuccessInformation?: {
          userInfo?: {
            userId?: string;
            [key: string]: unknown;
          };
          [key: string]: unknown;
        };
        [key: string]: unknown;
      };
      chatWindowInstance?: {
        updateConfig?: (config: Record<string, unknown>) => void;
        updateCustomData?: (data: Record<string, unknown>) => void;
        destroy?: () => void;
        _botInfo?: {
          customData?: Record<string, unknown>;
          [key: string]: unknown;
        };
        plugins?: {
          AgentDesktopPlugin?: {
            userInfo?: {
              userId?: string;
              [key: string]: unknown;
            };
            [key: string]: unknown;
          };
          [key: string]: unknown;
        };
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
  }
}

interface UseChatbotSDKInitParams {
  monarchFlags: UseChatbotMonarchReturnType;
}

/**
 * Hook to initialize and configure KORE Chat SDK with display modes from Monarch flags.
 * Supports standard, proactive, horizontal side rail, and vertical side rail display modes.
 * Watches for SDK availability and Monarch flags, then shows/hides chat icon accordingly.
 */
export const useChatbotSDKInit = ({ monarchFlags }: UseChatbotSDKInitParams): void => {
  const initializedRef = useRef(false);
  const lastConfigRef = useRef<string | undefined>(undefined);

  // Build chat icon config from monarch flags
  const config = useMemo(() => {
    const nextConfig: Record<string, unknown> = {};

    // Add position if present
    if (monarchFlags.position) {
      nextConfig.position = monarchFlags.position;
    }

    // Add trigger_type if present
    if (monarchFlags.triggerType) {
      nextConfig.trigger_type = monarchFlags.triggerType;
    }

    // Add proactive if present
    if (monarchFlags.proactive) {
      nextConfig.proactive = monarchFlags.proactive;
    }

    // Add page_type if present
    if (monarchFlags.pageType) {
      nextConfig.page_type = monarchFlags.pageType;
    }

    // Add ui_config if present
    if (monarchFlags.uiConfig) {
      nextConfig.ui_config = monarchFlags.uiConfig;
    }

    return nextConfig;
  }, [
    monarchFlags.position,
    monarchFlags.triggerType,
    monarchFlags.proactive,
    monarchFlags.pageType,
    monarchFlags.uiConfig,
  ]);

  // Serialize config for comparison
  const configString = useMemo(() => JSON.stringify(isEmpty(config) ? undefined : config), [config]);

  const isChatEnabled = !!monarchFlags.chatType;

  const showChatIconWithConfig = useCallback(() => {
    const success = showChatIcon(config);
    if (success) {
      initializedRef.current = true;
    }
    return success;
  }, [config]);

  const hideChatIconMethod = useCallback(() => {
    const success = hideChatIcon();
    if (success) {
      initializedRef.current = false;
    }
    return success;
  }, []);

  // Watch for SDK availability and initialize
  useEffect(() => {
    if (monarchFlags.isLoading || !isChatEnabled) {
      return;
    }

    let retryCount = 0;
    const maxRetries = 20;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const scheduleRetry = () => {
      timeoutId = setTimeout(checkAndInitialize, 500);
    };

    const clearScheduledRetry = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const checkAndInitialize = () => {
      if (window.KoreChatSDK) {
        const shown = showChatIconWithConfig();
        if (shown) {
          lastConfigRef.current = configString;
        } else if (retryCount < maxRetries) {
          // SDK is available but showChatIcon failed, retry
          retryCount++;
          scheduleRetry();
        }
      } else if (retryCount < maxRetries) {
        retryCount++;
        scheduleRetry();
      } else {
        console?.warn("KORE SDK failed to load after maximum retries");
      }
    };

    checkAndInitialize();

    return () => {
      clearScheduledRetry();
    };
  }, [monarchFlags.isLoading, isChatEnabled, configString, showChatIconWithConfig, monarchFlags.animate]);

  // Watch for config changes and update SDK dynamically
  useEffect(() => {
    if (monarchFlags.isLoading || !isChatEnabled || !initializedRef.current) {
      return;
    }

    // Check if config has changed
    if (configString === lastConfigRef.current) {
      return;
    }

    if (window.KoreChatSDK) {
      const shown = showChatIconWithConfig();
      if (shown) {
        lastConfigRef.current = configString;
      }
    }
  }, [monarchFlags.isLoading, isChatEnabled, configString, showChatIconWithConfig, monarchFlags.animate]);

  // Hide chat icon when disabled
  useEffect(() => {
    if (monarchFlags.isLoading) {
      if (initializedRef.current) {
        hideChatIconMethod();
      }
      initializedRef.current = false;
      lastConfigRef.current = undefined;
    }
  }, [monarchFlags.isLoading, hideChatIconMethod]);

  // Hide chat icon whenever feature flags disable chat after initialization
  useEffect(() => {
    if (monarchFlags.isLoading || isChatEnabled) {
      return;
    }

    if (initializedRef.current) {
      hideChatIconMethod();
    }
    initializedRef.current = false;
    lastConfigRef.current = undefined;
  }, [monarchFlags.isLoading, isChatEnabled, hideChatIconMethod]);
};
